import { v4 } from 'uuid';

import { useContracts } from '@/views/contracts/hooks/useContracts';
import type { OperatingCostInvoiceLineItemSimple } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import { usePointOfSales } from '@/views/pointofsales/hooks/usePointOfSales';
import { useSfmPositions } from '@/views/sfmpositions/hooks/useSfmPositions';
import { OperatingCostInvoiceLineItemV1Dto, OperatingCostInvoiceV1Dto } from '@/openapi';

/**
 * Mock AI Suggestion Data for Operating Cost Invoices
 *
 * Diese Daten simulieren die OCR-/KI-Erkennung aus einem Dokument.
 * Format: Ganze OperatingCostInvoiceSimple Objekte (wie historische Invoices)
 * Im späteren Zielformat: würde das vom Backend kommen.
 *
 * MOCK KENNZEICHNUNG: 🤖 Emoji im issuerLegalName zur visuellen Unterscheidung
 */

const MOCK_AI_INVOICE_SUGGESTIONS_BY_CONTRACT: Record<string, OperatingCostInvoiceSimple[]> = {
  // Default suggestions if no specific contract is found
  __default__: [
    {
      id: v4(),
      version: 0,
      issuerLegalName: '🤖 Berlin Water Works GmbH',
      issueDate: new Date().toISOString().split('T')[0],
      periodStart: '2026-01-01T00:00:00.000Z',
      periodEnd: '2026-12-31T00:00:00.000Z',
      documentReviewDeadline: '2026-02-28T00:00:00.000Z',
      objectionDeadline: '2026-03-31T00:00:00.000Z',
      internalObjectionDeadline: '2026-04-30T00:00:00.000Z',
      customerPrepaymentNet: 12500.5,
      customerPrepaymentGross: 14875.6,
      statedCustomerPrepaymentNet: 12000.0,
      settlementResultNet: 13245.75,
      settlementResultGross: 15762.44,
      costsNet: 55000.12,
      vatRate: 19,
      currency: 'BYN' as OperatingCostInvoiceV1Dto.currency,
      isHeatingCostsCompliantHKVO: false,
      pointOfSaleId: '0e7faff1-ddaf-4441-97bf-a0ef99b45f04',
      pointOfSaleName: '🤖 1370 - Berlin, Klosterstr. 3 (Spandau Arcaden) - Binta',
      contractId: '7434cabb-703a-40e2-995f-c913ee9b71cd',
      contractName: '🤖 AI Mock Contract',
      ticketId: undefined,
      areas: [],
      lineItems: [
        {
          id: v4(),
          version: 0,
          areaDesignation: 'Mietflaeche',
          nameOnStatement: '🤖 Heizung und Warmwasser',
          // Real SFM Position ID from backend: AzV (working child position)
          sfmPositionId: 'e69244bb-7970-4eb4-b3a8-ede166ec01b1',
          sfmPositionName: 'AzV',
          isFlatRate: false,
          totalAmount: 8500.5,
          customerAmount: 2250.75,
          totalArea: 5000,
          customerArea: 1200,
          status: OperatingCostInvoiceLineItemV1Dto.status.UNCLEAR,
        },
        {
          id: v4(),
          version: 0,
          areaDesignation: 'Pauschale',
          nameOnStatement: '🤖 Reinigung Allgemeinflächen',
          // Real SFM Position ID from backend: Reinigung und Pflege innen
          sfmPositionId: '2848a885-3a73-4e13-a382-8cd4e0fb6071',
          sfmPositionName: 'Reinigung und Pflege innen',
          isFlatRate: true,
          totalAmount: 3200.0,
          customerAmount: 850.0,
          status: OperatingCostInvoiceLineItemV1Dto.status.UNCLEAR,
        },
        {
          id: v4(),
          version: 0,
          areaDesignation: 'Mietflaeche',
          nameOnStatement: 'Wasser und Abwasser',
          // Real SFM Position ID from backend: Entsorgung sonstiges (child position)
          sfmPositionId: '9f1182ee-7df4-4737-9950-a2dd394faee8',
          sfmPositionName: 'Entsorgung sonstiges',
          isFlatRate: false,
          totalAmount: 3800.0,
          customerAmount: 980.0,
          status: OperatingCostInvoiceLineItemV1Dto.status.UNCLEAR,
        },
        {
          id: v4(),
          version: 0,
          areaDesignation: 'Mietflaeche',
          nameOnStatement: 'Schuttentsorgung',
          // Real SFM Position ID from backend: Entsorgung sonstiges (child position)
          sfmPositionId: '9f1182ee-7df4-4737-9950-a2dd394faee8',
          sfmPositionName: 'Entsorgung sonstiges',
          isFlatRate: false,
          totalAmount: 580.0,
          customerAmount: 100.0,
          status: OperatingCostInvoiceLineItemV1Dto.status.CLEAR,
        },
      ],
    },
  ],
};

interface SfmLookup {
  validIds: Set<string>;
  idByNormalizedName: Map<string, string>;
}

interface ContractMatch {
  id: string;
  name?: string;
}

interface PointOfSaleMatch {
  id: string;
  name?: string;
}

const MOCK_CONTRACT_NAME_MATCHERS = [
  'frental exymple',
  'frental example',
  'rental exymple',
  'rental example',
];

const normalizeSfmName = (value?: string): string => (value ?? '').trim().toLowerCase();
const normalizeContractName = (value?: string): string => (value ?? '').trim().toLowerCase();
const normalizePointOfSaleName = (value?: string): string =>
  (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

function findMockContractByName(
  contracts: Array<{ id?: string; name?: string }>,
): ContractMatch | null {
  const match = contracts.find((contract) => {
    const normalized = normalizeContractName(contract.name);
    if (!normalized) {
      return false;
    }
    return MOCK_CONTRACT_NAME_MATCHERS.some((matcher) => normalized.includes(matcher));
  });

  if (!match?.id) {
    return null;
  }

  return {
    id: match.id,
    name: match.name,
  };
}

function findMockPointOfSaleByName(
  pointOfSales: Array<{ id?: string; name?: string }>,
  targetName?: string,
): PointOfSaleMatch | null {
  const normalizedTarget = normalizePointOfSaleName(targetName);
  if (!normalizedTarget) {
    return null;
  }

  const exactMatch = pointOfSales.find(
    (pointOfSale) => normalizePointOfSaleName(pointOfSale.name) === normalizedTarget,
  );

  const partialMatch = pointOfSales.find((pointOfSale) => {
    const normalizedName = normalizePointOfSaleName(pointOfSale.name);
    if (!normalizedName) {
      return false;
    }

    return normalizedName.includes(normalizedTarget) || normalizedTarget.includes(normalizedName);
  });

  const match = exactMatch ?? partialMatch;
  if (!match?.id) {
    return null;
  }

  return {
    id: match.id,
    name: match.name,
  };
}

function buildSfmLookup(
  positions: Array<{ children?: Array<{ id: string; name: string }> }>,
): SfmLookup {
  const validIds = new Set<string>();
  const idByNormalizedName = new Map<string, string>();
  const ambiguousNames = new Set<string>();

  for (const parent of positions) {
    for (const child of parent.children ?? []) {
      validIds.add(child.id);

      const normalizedName = normalizeSfmName(child.name);
      if (!normalizedName) {
        continue;
      }

      const existing = idByNormalizedName.get(normalizedName);
      if (!existing) {
        idByNormalizedName.set(normalizedName, child.id);
        continue;
      }

      if (existing !== child.id) {
        // Keep mapping deterministic: ambiguous names are removed to avoid wrong assignments.
        idByNormalizedName.delete(normalizedName);
        ambiguousNames.add(normalizedName);
      }
    }
  }

  return {
    validIds,
    idByNormalizedName,
  };
}

function reconcileLineItemSfmPosition(
  lineItem: OperatingCostInvoiceLineItemSimple,
  sfmLookup: SfmLookup,
): OperatingCostInvoiceLineItemSimple {
  if (sfmLookup.validIds.has(lineItem.sfmPositionId)) {
    return lineItem;
  }

  const mappedId = sfmLookup.idByNormalizedName.get(normalizeSfmName(lineItem.sfmPositionName));
  if (!mappedId) {
    return lineItem;
  }

  return {
    ...lineItem,
    sfmPositionId: mappedId,
  };
}

/**
 * Hook: useMockAiSuggestions
 *
 * Simuliert einen Backend-Call zur KI-gestützten Dokumentenerkennung
 * (OCR-Erkennung aus dem hochgeladenen PDF-Dokument)
 */
export function useMockAiSuggestions() {
  const { fetchContracts } = useContracts();
  const { fetchPointOfSales } = usePointOfSales();
  const { fetchSfmPositions } = useSfmPositions();

  let sfmLookupPromise: Promise<SfmLookup> | null = null;
  let mockContractPromise: Promise<ContractMatch | null> | null = null;
  let pointOfSalesPromise: Promise<Array<{ id?: string; name?: string }>> | null = null;

  const getSfmLookup = async (): Promise<SfmLookup> => {
    if (!sfmLookupPromise) {
      sfmLookupPromise = (async () => {
        const sfmPositions = (await fetchSfmPositions()) ?? [];
        return buildSfmLookup(sfmPositions);
      })();
    }

    return sfmLookupPromise;
  };

  const getMockContract = async (): Promise<ContractMatch | null> => {
    if (!mockContractPromise) {
      mockContractPromise = (async () => {
        try {
          const contracts = (await fetchContracts()) ?? [];
          return findMockContractByName(contracts);
        } catch (err) {
          console.log(err);
          return null;
        }
      })();
    }

    return mockContractPromise;
  };

  const getPointOfSales = async (): Promise<Array<{ id?: string; name?: string }>> => {
    if (!pointOfSalesPromise) {
      pointOfSalesPromise = (async () => {
        const pointOfSalesResponse = await fetchPointOfSales({});
        const pointOfSales = pointOfSalesResponse?.data ?? [];

        return pointOfSales.map((pointOfSale) => ({
          id: pointOfSale.id,
          name: pointOfSale.name,
        }));
      })();
    }

    return pointOfSalesPromise ?? Promise.resolve([]);
  };

  /**
   * Fetch AI suggestions for a given document
   *
   * @param documentId - The document ID (from the ticket's utility bills)
   * @returns Promise resolving to suggested Operating Cost Invoices with lineItems marked as 'ai'
   *
   * Zukünftig:
   * - Real API call to Backend AI/OCR service with documentId
   * - Response parsing & mapping to OperatingCostInvoiceSimple
   * - Error handling & fallback to empty array
   */
  const getAiSuggestionsForDocument = async (
    ticketId?: string | null,
    documentId?: string | null,
  ): Promise<OperatingCostInvoiceSimple[]> => {
    // Simulate async API delay (real network latency)
    return new Promise((resolve) => {
      setTimeout(() => {
        void (async () => {
          // For now: Always return default suggestions
          // Future improvement: Use documentId to fetch from Backend AI/OCR service
          if (!documentId) {
            // console.log('[useMockAiSuggestions] No documentId provided, returning empty');
            resolve([]);
            return;
          }

          // console.log(
          //   '[useMockAiSuggestions] Fetching mock AI suggestions for documentId:',
          //   documentId,
          // );
          const suggestions = MOCK_AI_INVOICE_SUGGESTIONS_BY_CONTRACT.__default__;

          // Reconcile stale seeded SFM IDs against current backend positions by position name.
          const sfmLookup = await getSfmLookup();
          const mockContract = await getMockContract();
          const pointOfSales = await getPointOfSales();

          // Generate fresh IDs and versions on each call to avoid UUID collisions
          const freshSuggestions = suggestions.map((s) => {
            const mockPointOfSale = findMockPointOfSaleByName(pointOfSales, s.pointOfSaleName);

            const suggestion: OperatingCostInvoiceSimple = {
              ...s,
              id: v4(),
              version: 0,
              pointOfSaleId: mockPointOfSale?.id ?? s.pointOfSaleId,
              pointOfSaleName: mockPointOfSale?.name ?? s.pointOfSaleName,
              contractId: mockContract?.id ?? s.contractId,
              contractName: mockContract?.name ?? s.contractName,
              ticketId: ticketId ?? undefined,
              lineItems: (s.lineItems ?? []).map((li) =>
                reconcileLineItemSfmPosition(
                  {
                    ...li,
                    id: v4(),
                    version: 0,
                  },
                  sfmLookup,
                ),
              ),
            };

            return suggestion;
          });

          resolve(freshSuggestions);
        })();
      }, 300); // Simulate 300ms network delay
    });
  };

  return {
    getAiSuggestionsForDocument,
  };
}
